import { 
  AtSymbolIcon, 
  NewspaperIcon, 
  PhoneIcon,
  UserCircleIcon,
  UsersIcon,
  VideoCameraIcon, 
} from '@heroicons/react/24/outline';
import React from 'react';

import { ContactInteractionType } from '../../../../@types/contactInteraction';
import type { Contact } from '../../../../@types/Owner';
import type { UnifiedContact } from '../../../../utils/contactUtils';
import { getMeetingTypeInfo } from '../../../../utils/contactUtils';
import AccessibleEmoji from '../../../common/AccessibleEmoji';
import Popup from '../../../common/Popup/Popup.module';
import { formatDateFromIsoString } from '../../utils/formatters';

interface PreviousContactListPopupProps {
  isOpen: boolean;
  onClose: () => void;
  contacts?: Contact[];
  mergedContacts?: UnifiedContact[];
  ownerName: string;
}

const PreviousContactListPopup: React.FC<PreviousContactListPopupProps> = ({
  isOpen,
  onClose,
  contacts,
  mergedContacts,
  ownerName,
}) => {
  // Use mergedContacts if provided, otherwise sort the regular contacts
  const sortedContacts = mergedContacts || 
    (contacts ? [...contacts].sort((a, b) => {
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    }) : []);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  // Get meeting type emoji and label based on contact type
  // Removed local getMeetingTypeInfo function - now using it from utils

  const getContactTypeIcon = (contact: UnifiedContact | Contact) => {
    // If it's an interaction (not a regular contact), use UsersIcon with yellow color
    if ('source' in contact && contact.source !== 'contact') {
      return <UsersIcon className="h-5 w-5 shrink-0 text-amber-500" />;
    }
    
    // For regular contacts, use the standard icons
    switch (contact.type) {
    case ContactInteractionType.MAIL:
    case ContactInteractionType.MAIL_FOR_CONSENT:
    case ContactInteractionType.MAIL_FOLLOW_UP:
      return <AtSymbolIcon className="h-5 w-5 shrink-0 text-blue-600" />;
    case ContactInteractionType.PAPER:
    case ContactInteractionType.PAPER_FOR_CONSENT:
      return <NewspaperIcon className="h-5 w-5 shrink-0 text-amber-600" />;
    case ContactInteractionType.PHONE:
      return <PhoneIcon className="h-5 w-5 shrink-0 text-red-600" />;
    case ContactInteractionType.VISIO:
      return <VideoCameraIcon className="h-5 w-5 shrink-0 text-purple-600" />;
    case ContactInteractionType.PHYSICAL:
      return <UserCircleIcon className="h-5 w-5 shrink-0 text-indigo-600" />;
    default:
      return <PhoneIcon className="h-5 w-5 shrink-0 text-gray-600" />;
    }
  };

  const getContactTypeLabel = (contact: UnifiedContact | Contact) => {
    // If it's an interaction (not a regular contact), just return "Interaction"
    if ('source' in contact && contact.source !== 'contact') {
      return 'Interaction';
    }
    
    // For regular contacts, use the standard labels
    const contactTypeMap = {
      [ContactInteractionType.MAIL]: 'Email',
      [ContactInteractionType.MAIL_FOR_CONSENT]: 'Email de consentement',
      [ContactInteractionType.MAIL_FOLLOW_UP]: 'Email de relance',
      [ContactInteractionType.PAPER]: 'Courrier',
      [ContactInteractionType.PAPER_FOR_CONSENT]: 'Courrier de consentement',
      [ContactInteractionType.PHONE]: 'Téléphone',
      [ContactInteractionType.VISIO]: 'Visio',
      [ContactInteractionType.PHYSICAL]: 'Présentiel',
    };
    
    return contactTypeMap[contact.type] || contact.type.replaceAll('-', ' ');
  };

  const getContactCardClassName = (contact: UnifiedContact | Contact) => {
    // If it's an interaction (not a regular contact), use amber/yellow styling
    if ('source' in contact && contact.source !== 'contact') {
      return 'border-amber-200 bg-amber-50';
    }
    
    // For regular contacts, use the standard styling
    switch (contact.type) {
    case ContactInteractionType.MAIL:
    case ContactInteractionType.MAIL_FOR_CONSENT:
    case ContactInteractionType.MAIL_FOLLOW_UP:
      return 'border-blue-200 bg-blue-50';
    case ContactInteractionType.PAPER:
    case ContactInteractionType.PAPER_FOR_CONSENT:
      return 'border-amber-200 bg-amber-50';
    case ContactInteractionType.PHONE:
      return 'border-red-200 bg-red-50';
    case ContactInteractionType.VISIO:
      return 'border-purple-200 bg-purple-50';
    case ContactInteractionType.PHYSICAL:
      return 'border-indigo-200 bg-indigo-50';
    default:
      return 'border-gray-200 bg-gray-50';
    }
  };

  // Get source badge if applicable (for unified contacts)
  const getSourceBadge = (contact: UnifiedContact | Contact) => {
    if ('source' in contact && contact.source !== 'contact') {
      const sourceLabels = {
        qualityReference: 'Référent mutation location',
        rentManager: 'Gestionnaire locatif',
        salesManager: 'Transactionnaire',
        lead: 'Clôture',
      };

      return (
        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          {sourceLabels[contact.source]}
        </span>
      );
    }
    return null;
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      width="600px"
      maxHeight="80vh"
      title={`Historique des contacts - ${ownerName}`}
    >
      <div className="max-h-[60vh] overflow-y-auto">
        {sortedContacts.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            Aucun historique de contact disponible
          </div>
        ) : (
          <div className="space-y-4">
            {sortedContacts.map((contact) => (
              <div 
                key={contact.id} 
                className={`rounded-lg border p-4 ${getContactCardClassName(contact)} shadow-sm`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">{getContactTypeIcon(contact)}</div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <h3 className="font-medium text-gray-900">
                          {getContactTypeLabel(contact)}
                        </h3>
                        {getSourceBadge(contact)}
                      </div>
                      <span className="text-sm text-gray-600">
                        {formatDateFromIsoString(contact.createdAt)}
                      </span>
                    </div>
                    
                    {contact.user && (
                      <div className="mt-2 flex items-center text-sm text-gray-700">
                        <UserCircleIcon className="mr-1 h-4 w-4 shrink-0 text-gray-500" />
                        <span>
                          {contact.user.firstname} {contact.user.lastname}
                        </span>
                      </div>
                    )}

                    {/* Show meeting information if available */}
                    {'meetingAt' in contact && contact.meetingAt && (
                      <div className="mt-2 text-sm text-gray-700">
                        <div className="font-medium">Rendez-vous prévu :</div>
                        <div className="mt-0.5 text-xs">{formatDate(contact.meetingAt)}</div>
                        <div className="mt-0.5 flex items-center text-xs">
                          {(() => {
                            const { emoji, label } = getMeetingTypeInfo(contact.type);
                            return (
                              <>
                                <AccessibleEmoji symbol={emoji} label="meeting type icon" className="mr-1" />
                                <span>Type : {label}</span>
                              </>
                            );
                          })()}
                        </div>
                        {contact.meetingWith && (
                          <div className="mt-0.5 flex items-center text-xs">
                            <UserCircleIcon className="mr-1 h-3 w-3 shrink-0 text-gray-500" />
                            <span>
                              Avec : {contact.meetingWith.firstname} {contact.meetingWith.lastname}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Show comment if available */}
                    {'comment' in contact && contact.comment && (
                      <div className="mt-2 text-sm">
                        <div className="font-medium text-gray-700">Commentaire :</div>
                        <div className="mt-0.5 text-xs text-gray-600">{contact.comment}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Popup>
  );
};

export default PreviousContactListPopup; 